import React, { Fragment, useEffect, useState } from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { withRouter, Link } from "react-router-dom";
import Select from "react-select";
import { CircularProgress } from "@material-ui/core";
import ConfirmDialog from "../../components/ConfirmDialog";
import Icon from "../../components/Icon";
import { SurveyReport } from "../SurveyInsight/SurveyInsight";
import Images from "../../utils/styles/images";
import {
  REQNotificationList,
  SENDxNotification,
  DELxNotification,
  SETxNotificationStatus,
  CleanUpNotificationList,
  REQEventCounts,
  REQCsatSetting,
  REQSaveCsatSetting
} from "./redux/actions";
import { AUTO_REFRESH_MS, SEND_STATUS_LABEL } from "./api";
import "./style.scss";

// Status filter — server-side (sent as sendStatus; "ALL" means the filter is off).
const STATUS_OPTIONS = [
  { value: "ALL", label: "All statuses" },
  { value: "DRAFT", label: "Draft" },
  { value: "PENDING", label: "Pending" },
  { value: "SENDING", label: "Sending" },
  { value: "SENT", label: "Sent" },
  { value: "PARTIAL", label: "Partially sent" },
  { value: "FAILED", label: "Failed" },
  { value: "CANCELLED", label: "Cancelled" }
];

// Content type filter — client-side (the list API has no content-type parameter).
const CONTENT_TYPE_OPTIONS = [
  { value: "", label: "All types" },
  { value: "GENERAL", label: "General" },
  { value: "PROMO", label: "Promo" },
  { value: "URGENT", label: "Urgent" }
];

// CTA (actionType) filter — client-side, same as the content-type one.
const CTA_FILTER_OPTIONS = [
  { value: "", label: "All CTA" },
  { value: "NONE", label: "None" },
  { value: "URL", label: "Link" },
  { value: "DEEPLINK", label: "In-app" },
  { value: "SURVEY", label: "Survey" }
];

/*
 * The two tabs: campaigns an operator composed (Manual) and notifications raised by a backend
 * event (Automatic). Filtered by the list API's `origin`, so each tab pages and counts on its own.
 * The tab is kept in the URL (?source=automatic) so a refresh or a shared link reopens it.
 * Automatic is view-only: no Create new, no row actions.
 */
const TABS = [
  { value: "manual", label: "Manual", origin: "ADMIN" },
  { value: "automatic", label: "Automatic", origin: "AUTOMATED" }
];

// be-notification's NotificationEventType, labelled for the Automatic tab's Event column / filter.
const EVENT_LABEL = {
  ACCOUNT_OPENING_PROGRESS: "Account opening progress",
  ACCOUNT_APPROVED: "Account approved",
  ACCOUNT_REJECTED_ID: "Account rejected (ID)",
  MF_TRANSACTION_STATUS: "MF transaction status",
  PVD_TRANSACTION_STATUS: "PVD transaction status",
  PVD_REGISTRATION_SUCCESS: "PVD registration success",
  NDID_ABANDONED: "NDID abandoned",
  KYC_UPDATED: "KYC updated",
  PASSWORD_CHANGED: "Password changed",
  NEW_DEVICE_LOGIN: "New device login",
  MOBILE_CHANGED: "Mobile changed",
  CRS_FATCA_UPDATED: "CRS / FATCA updated",
  RSP_STATUS: "RSP status",
  REDEMPTION_BANK_STATUS: "Redemption bank status",
  SUITABILITY_UPDATED: "Suitability updated",
  SOFT_ONBOARD_SUCCESS: "Soft onboard success",
  CSAT_MF_ACCOUNT_APPROVED: "CSAT: MF account approved",
  CSAT_PVD_ACCOUNT_OPENED: "CSAT: PVD account opened",
  CSAT_FIRST_MF_TRANSACTION: "CSAT: first MF transaction",
  CSAT_FIRST_PVD_TRANSACTION: "CSAT: first PVD transaction",
  CSAT_VIEW_PORTFOLIO: "CSAT: view portfolio"
};

const EVENT_FILTER_OPTIONS = [
  { value: "", label: "All events" },
  ...Object.keys(EVENT_LABEL).map(value => ({ value, label: EVENT_LABEL[value] }))
];

const PAGE_SIZE = 20;

const CONTENT_TYPE_LABEL = {
  GENERAL: "General",
  PROMO: "Promo",
  URGENT: "Urgent"
};

/*
 * CTA = the "Button in the message" (actionType) a notification carries. Shown as plain text —
 * a short label per kind, or a dash for NONE. The destination (actionLink) stays as a tooltip.
 * For a survey, the survey code (actionLink) is read to name the kind: "Survey - CSAT" / "- NPS".
 */
const CTA_LABEL = {
  URL: "Link",
  DEEPLINK: "In-app",
  SURVEY: "Survey"
};

const ctaText = notification => {
  const type = notification.actionType;
  if (!type || type === "NONE") {
    return "—";
  }
  if (type === "SURVEY") {
    const link = notification.actionLink || "";
    const kind = /csat/i.test(link) ? "CSAT" : /nps/i.test(link) ? "NPS" : "";
    return kind ? `Survey - ${kind}` : "Survey";
  }
  return CTA_LABEL[type] || type;
};

// The Consent page's View action (eye icon, underlined blue text), for a row that can only be
// viewed: every automatic notification, and a manual one once it was sent. Opens it read-only.
const viewLink = notification => (
  <Link
    className="nl-action-view"
    to={`/PushNoti_Compose?id=${notification.id}`}
    aria-label={`View ${notification.header}`}
  >
    <img src={Images.fundListIcon.view} alt="" />
    View
  </Link>
);

/*
 * Each confirm maps to a short sentence-case title, an optional explanatory body, and the label on
 * the confirming (right) button so it says what it does rather than a generic "Yes". The dismissing
 * (left) button is "Cancel" by default, overridden where that word would clash with the action
 * itself (cancelling a scheduled send). This feeds the shared ConfirmDialog, so every prompt here
 * matches the "Discard unsaved changes?" card — short heading, body line, left/right buttons.
 */
const CONFIRM_META = {
  SEND: notification => ({
    title: `Send "${notification.header}" now?`,
    body: `It is sent to the ${notification.userScope} customer group right now and cannot be unsent.`,
    confirm: "Send now"
  }),
  DELETE: notification => ({
    title: `Delete "${notification.header}"?`,
    body: "This removes the notification from the list. This cannot be undone.",
    confirm: "Delete"
  }),
  // A scheduled send that has not gone out yet: cancelling keeps the record but stops the send.
  CANCEL: notification => ({
    title: `Cancel the scheduled send of "${notification.header}"?`,
    body: "It stays in the list as cancelled and will not be sent.",
    confirm: "Cancel send",
    dismiss: "Keep scheduled"
  }),
  RESUME: notification => ({
    title: `Resume the scheduled send of "${notification.header}"?`,
    body: "It sends at its scheduled time (or now if that time has passed).",
    confirm: "Resume"
  })
};

const formatDate = value => {
  if (!value) {
    return "—";
  }
  const date = new Date(value);
  return isNaN(date.getTime()) ? "—" : date.toLocaleString("th-TH");
};

const formatCount = value => (value == null ? "—" : Number(value).toLocaleString());

/*
 * An admin's username (their Okta login) from the backend audit trail, or "" when the value
 * names no one an operator would recognise: event-raised rows are recorded as "system", and
 * rows saved before the login was recorded hold the admin's customerId GUID instead.
 */
const SYSTEM_ACTOR = "system";
const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const adminUsername = value => {
  const actor = (value || "").trim();
  return actor && actor !== SYSTEM_ACTOR && !GUID_PATTERN.test(actor) ? actor : "";
};

/*
 * A campaign that has gone out can no longer be edited or deleted; it is history.
 *
 * The test is the send time, not the send status: a campaign whose pushes all failed
 * still reached every recipient's inbox and may already have been read. This mirrors
 * the backend guard, which refuses the edit either way.
 */
const isDelivered = notification => Boolean(notification.timeSent);

/*
 * A campaign that is queued to go out but has not been sent yet: scheduled (a Send date in
 * the future) or otherwise pending. These are the ones an operator can still cancel.
 */
const isScheduledPending = notification =>
  !isDelivered(notification) && notification.sendStatus === "PENDING";

/*
 * A scheduled send the operator has cancelled. Cancelling flips the campaign to INACTIVE,
 * which the scheduler never picks up (it only dispatches ACTIVE + PENDING rows), so the
 * record and its Send date stay while the send is stopped. Resuming turns it back to ACTIVE.
 */
const isCancelledSchedule = notification =>
  isScheduledPending(notification) && notification.status !== "ACTIVE";

/*
 * The send status an operator sees, folding the cancelled-schedule case (INACTIVE + PENDING)
 * into CANCELLED. Drives both the badge label and its colour class (s-draft, s-sent, …).
 */
const effectiveStatus = notification =>
  isCancelledSchedule(notification) ? "CANCELLED" : notification.sendStatus;

function NotificationList(props) {
  const {
    REQNotificationList,
    SENDxNotification,
    DELxNotification,
    SETxNotificationStatus,
    CleanUpNotificationList,
    REQStrNotificationList,
    NotificationList,
    Pagination,
    errorMessage,
    history,
    location
  } = props;

  // The sidebar's Manual / Automatic entries pass `source`; the tab then comes from the menu, not the URL.
  const tab = TABS.find(item => item.value === (props.source || new URLSearchParams(location.search).get("source"))) || TABS[0];
  const automaticTab = tab.value === "automatic";

  const [oSearch, SETxSearch] = useState("");
  const [oSubmittedSearch, SETxSubmittedSearch] = useState("");
  const [oFilter, SETxFilter] = useState("ALL");
  const [oPage, SETxPage] = useState(1);
  const [oConfirm, SETxConfirm] = useState(null);
  // The row whose actions (⋮) menu is open, or null.
  const [oMenu, SETxMenu] = useState(null);
  // The row menu opens upward for a row near the bottom, so the scrolling table box never clips it.
  const [oMenuUp, SETxMenuUp] = useState(false);
  const toggleMenu = (event, id) => {
    if (oMenu === id) {
      SETxMenu(null);
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    SETxMenuUp(window.innerHeight - rect.bottom < 210);
    SETxMenu(id);
  };
  // Event filter (Automatic tab) - server-side, like status and search.
  const [oEventType, SETxEventType] = useState(props.eventType || "");
  // Client-side filters (the list API has no content-type / CTA / date parameters).
  const [oContentType, SETxContentType] = useState("");
  const [oCta, SETxCta] = useState("");
  // Created by (Manual tab) - the admin's username, matched on the rows of the current page.
  const [oCreator, SETxCreator] = useState("");
  const [oDateFrom, SETxDateFrom] = useState("");
  const [oDateTo, SETxDateTo] = useState("");

  const filters = {
    search: oSubmittedSearch,
    sendStatus: oFilter,
    origin: tab.origin,
    eventType: automaticTab ? oEventType : "",
    page: oPage,
    limit: PAGE_SIZE
  };

  useEffect(() => {
    REQNotificationList(filters);
    return () => {
      CleanUpNotificationList();
    };
  }, [oSubmittedSearch, oFilter, oPage, tab.value, oEventType]);

  // Switching tab starts it from page 1 and drops the filters only the other tab has.
  const onTab = value => {
    if (value === tab.value) {
      return;
    }
    SETxMenu(null);
    SETxPage(1);
    SETxEventType("");
    SETxContentType("");
    SETxCta("");
    SETxCreator("");
    history.replace(value === "automatic" ? `${location.pathname}?source=automatic` : location.pathname);
  };

  /*
   * Notifications raised by backend events arrive without anyone on this screen doing anything,
   * so the page re-reads itself every AUTO_REFRESH_MS. Paused while a load is running (the timer
   * restarts once it ends) and while the browser tab is hidden.
   */
  useEffect(() => {
    if (REQStrNotificationList) {
      return undefined;
    }
    const timerId = setInterval(() => {
      if (!document.hidden) {
        REQNotificationList({ ...filters, silent: true });
      }
    }, AUTO_REFRESH_MS);
    return () => clearInterval(timerId);
  }, [oSubmittedSearch, oFilter, oPage, tab.value, oEventType, REQStrNotificationList]);

  // Close the row actions menu on an outside click or Escape.
  useEffect(() => {
    if (!oMenu) {
      return undefined;
    }
    const close = event => {
      if (!event.target.closest || !event.target.closest(".nl-menu-wrap")) {
        SETxMenu(null);
      }
    };
    const esc = event => {
      if (event.key === "Escape") {
        SETxMenu(null);
      }
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", esc);
    };
  }, [oMenu]);

  const onSearch = event => {
    event.preventDefault();
    SETxPage(1);
    SETxSubmittedSearch(oSearch.trim());
  };

  // Reset every filter (search box, the dropdowns, and the date range) back to its default.
  const onClearFilters = () => {
    SETxSearch("");
    SETxSubmittedSearch("");
    SETxFilter("ALL");
    SETxEventType("");
    SETxContentType("");
    SETxCta("");
    SETxCreator("");
    SETxDateFrom("");
    SETxDateTo("");
    SETxPage(1);
  };

  const onConfirm = () => {
    if (!oConfirm) {
      return;
    }
    if (oConfirm.action === "SEND") {
      SENDxNotification({ id: oConfirm.notification.id, filters });
    }
    if (oConfirm.action === "DELETE") {
      DELxNotification({ id: oConfirm.notification.id, filters });
    }
    // Cancel / resume a scheduled send: the same status flip, kept apart so the confirm copy fits.
    if (oConfirm.action === "CANCEL") {
      SETxNotificationStatus({ id: oConfirm.notification.id, status: "INACTIVE", filters });
    }
    if (oConfirm.action === "RESUME") {
      SETxNotificationStatus({ id: oConfirm.notification.id, status: "ACTIVE", filters });
    }
    SETxConfirm(null);
  };

  // Open the confirm dialog for a row action and close the menu.
  const ask = (action, notification) => {
    SETxMenu(null);
    SETxConfirm({ action, notification });
  };

  const rows = NotificationList || [];

  // Content type, CTA, created by and date range are applied here, on the current page, because the list API
  // filters only by tab (origin), event, status and search, which come filtered from the backend.
  const visibleRows = rows.filter(notification => {
    // A response for the other tab can still land after a switch (the page that asked for it has gone),
    // so each row is also checked against this tab's origin.
    if (notification.origin !== tab.origin) {
      return false;
    }
    if (oContentType && notification.contentType !== oContentType) {
      return false;
    }
    // A row with no action recorded is treated as NONE, so the "None" filter catches old rows.
    if (oCta && (notification.actionType || "NONE") !== oCta) {
      return false;
    }
    if (oCreator && adminUsername(notification.createdBy) !== oCreator) {
      return false;
    }
    const day = String(notification.timeSent || notification.scheduledAt || "").slice(0, 10);
    if (oDateFrom && (!day || day < oDateFrom)) {
      return false;
    }
    if (oDateTo && (!day || day > oDateTo)) {
      return false;
    }
    return true;
  });

  const hasFilters =
    oSubmittedSearch ||
    oFilter !== "ALL" ||
    oEventType ||
    oContentType ||
    oCta ||
    oCreator ||
    oDateFrom ||
    oDateTo;

  // The admins behind the loaded rows, for the Created by filter. The one chosen stays listed even
  // when the page has none of theirs, so the dropdown still shows what is selected.
  const creatorNames = Array.from(
    new Set([...rows.map(notification => adminUsername(notification.createdBy)), oCreator].filter(Boolean))
  ).sort();
  const CREATOR_FILTER_OPTIONS = [
    { value: "", label: "All admins" },
    ...creatorNames.map(name => ({ value: name, label: name }))
  ];

  const totalPages = (Pagination && Pagination.totalPages) || 0;
  const total = (Pagination && Pagination.total) || 0;
  const rangeStart = total === 0 ? 0 : (oPage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(oPage * PAGE_SIZE, total);

  const statusBadge = notification => {
    const status = effectiveStatus(notification);
    const label = SEND_STATUS_LABEL[status] || status;
    const className = `nl-status-badge s-${(status || "").toLowerCase()}`;
    // A delivered campaign's status opens its delivery log: who it reached and who it did not.
    if (isDelivered(notification)) {
      return (
        <Link
          className={className}
          title="View history"
          to={`/Notification_DeliveryLog?id=${notification.id}`}
        >
          {label}
        </Link>
      );
    }
    return <span className={className}>{label}</span>;
  };

  return (
    <Fragment>
      {REQStrNotificationList ? (
        <div className="funds-list-progress">
          <CircularProgress />
          <p className="funds-list-progress-text">Loading notifications...</p>
        </div>
      ) : null}

      {/*
        * No heading: the breadcrumb already names the page. "Create new" sits on the breadcrumb's
        * line at the right, where CANCEL / SAVE DRAFT are on the compose page it opens.
        */}
      <div className="noti-list-container noti-list-container-actions-only">
        <div className="noti-list-topbar noti-list-topbar-actions-only">
          {/* Automatic notifications are raised by events, never composed here. */}
          {automaticTab ? null : (
            <div className="noti-list-actions">
              <Link className="noti-list-create" to="/PushNoti_Compose">
                <Icon name="plus" size={18} />
                Create new
              </Link>
            </div>
          )}
        </div>

        {errorMessage ? <div className="noti-list-error">{errorMessage}</div> : null}

        <div className="nl-filter-row">
          <div className="nl-filter-field nl-filter-field-wide">
            <span className="nl-filter-label">Search</span>
            <form className="nl-search" onSubmit={onSearch}>
              <Icon name="search" size={18} />
              <input
                type="search"
                // The backend also matches the Event, Type, CTA and Status columns by what they show,
                // and Created by; Event is on the Automatic tab only, Created by on the Manual tab.
                placeholder={
                  automaticTab
                    ? "Search by name, title, event, type, CTA, status…"
                    : "Search by name, title, type, CTA, status, created by…"
                }
                value={oSearch}
                onChange={event => SETxSearch(event.target.value)}
              />
            </form>
          </div>
          {automaticTab ? (
            <div className="nl-filter-field nl-filter-field-event">
              <span className="nl-filter-label">Event</span>
              <Select
                className="basic-single"
                classNamePrefix="select"
                aria-label="Filter by event"
                isSearchable={false}
                options={EVENT_FILTER_OPTIONS}
                value={EVENT_FILTER_OPTIONS.find(option => option.value === oEventType) || null}
                onChange={option => {
                  SETxPage(1);
                  SETxEventType(option ? option.value : "");
                }}
              />
            </div>
          ) : (
            <Fragment>
              <div className="nl-filter-field">
                <span className="nl-filter-label">Type</span>
                <Select
                  className="basic-single"
                  classNamePrefix="select"
                  aria-label="Filter by type"
                  isSearchable={false}
                  options={CONTENT_TYPE_OPTIONS}
                  value={CONTENT_TYPE_OPTIONS.find(option => option.value === oContentType) || null}
                  onChange={option => SETxContentType(option ? option.value : "")}
                />
              </div>
              <div className="nl-filter-field">
                <span className="nl-filter-label">CTA</span>
                <Select
                  className="basic-single"
                  classNamePrefix="select"
                  aria-label="Filter by CTA"
                  isSearchable={false}
                  options={CTA_FILTER_OPTIONS}
                  value={CTA_FILTER_OPTIONS.find(option => option.value === oCta) || null}
                  onChange={option => SETxCta(option ? option.value : "")}
                />
              </div>
            </Fragment>
          )}
          <div className="nl-filter-field">
            <span className="nl-filter-label">Status</span>
            <Select
              className="basic-single"
              classNamePrefix="select"
              aria-label="Filter by status"
              isSearchable={false}
              options={STATUS_OPTIONS}
              value={STATUS_OPTIONS.find(option => option.value === oFilter) || null}
              onChange={option => {
                SETxPage(1);
                SETxFilter(option ? option.value : "ALL");
              }}
            />
          </div>
          {automaticTab ? null : (
            <div className="nl-filter-field nl-filter-field-creator">
              <span className="nl-filter-label">Created by</span>
              <Select
                className="basic-single"
                classNamePrefix="select"
                aria-label="Filter by created by"
                options={CREATOR_FILTER_OPTIONS}
                value={CREATOR_FILTER_OPTIONS.find(option => option.value === oCreator) || null}
                onChange={option => SETxCreator(option ? option.value : "")}
              />
            </div>
          )}
          <div className="nl-filter-field nl-filter-field-date">
            <span className="nl-filter-label">From</span>
            <input
              className="nl-date-input"
              type="date"
              aria-label="From date"
              value={oDateFrom}
              onChange={event => SETxDateFrom(event.target.value)}
            />
          </div>
          <div className="nl-filter-field nl-filter-field-date">
            <span className="nl-filter-label">To</span>
            <input
              className="nl-date-input"
              type="date"
              aria-label="To date"
              min={oDateFrom}
              value={oDateTo}
              onChange={event => SETxDateTo(event.target.value)}
            />
          </div>
          <div className="nl-filter-actions">
            <button type="button" className="nl-clear" onClick={onClearFilters} disabled={!hasFilters}>
              <Icon name="close" size={18} />
              Clear filters
            </button>
          </div>
        </div>

        {/* The Automatic page passes its tab strip here, under the filters. */}
        {props.tabs}
        {/* On a sidebar page each tab is its own menu entry, so there is no tab strip there. */}
        {props.source ? null : (
          <div className="nl-tabs" role="tablist" aria-label="Notification source">
            {TABS.map(item => (
              <button
                key={item.value}
                type="button"
                role="tab"
                aria-selected={item.value === tab.value}
                className={item.value === tab.value ? "nl-tab active" : "nl-tab"}
                onClick={() => onTab(item.value)}
              >
                {item.label}
              </button>
            ))}
          </div>
        )}

        {/* The open tab's white panel: the table and pager sit on it, joined to the tab. */}
        <div className="nl-panel" role="tabpanel">
          <div className="nl-table-wrap nl-list-table">
            <table>
              <thead>
                <tr>
                  {/* An automatic row's code is a generated AUTO-... id, so the History shows the event instead. */}
                  {automaticTab ? <th>Event</th> : <th>Code</th>}
                  <th>Type</th>
                  <th>CTA</th>
                  <th>Notification</th>
                  <th>Time sent</th>
                  <th className="nl-num">Reach</th>
                  <th className="nl-num">View</th>
                  <th className="nl-num">Click</th>
                  <th className="nl-status-cell">Status</th>
                  {/* Automatic rows are all "system": no audit columns. */}
                  {automaticTab ? null : (
                    <Fragment>
                      <th>Created by</th>
                      <th>Updated</th>
                    </Fragment>
                  )}
                  <th className="nl-actions-head">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map(notification => {
                  const delivered = isDelivered(notification);
                  const scheduled = isScheduledPending(notification);
                  const cancelled = isCancelledSchedule(notification);
                  const creator = adminUsername(notification.createdBy);
                  return (
                    <tr key={notification.id}>
                      {automaticTab ? (
                        <td className="nl-event-cell" title={notification.eventType || ""}>
                          {EVENT_LABEL[notification.eventType] || notification.eventType || "—"}
                        </td>
                      ) : (
                        <td className="nl-code" title={notification.code}>
                          {notification.code || "—"}
                        </td>
                      )}
                      <td className="nl-type-cell">
                        {CONTENT_TYPE_LABEL[notification.contentType] || notification.contentType}
                      </td>
                      <td className="nl-cta-cell" title={notification.actionLink || ""}>
                        {ctaText(notification)}
                      </td>
                      <td className="nl-noti-cell">
                        {/* Opens read-only for a sent or an automatic notification. */}
                        <Link className="nl-name-btn" to={`/PushNoti_Compose?id=${notification.id}`}>
                          {notification.header}
                        </Link>
                        {notification.content ? (
                          <div className="nl-noti-body">{notification.content}</div>
                        ) : null}
                        <div className="nl-sub">
                          {[
                            // The Automatic tab shows the event in its own column.
                            automaticTab ? null : notification.eventType,
                            notification.name,
                            notification.notificationType,
                            notification.userScope
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </div>
                      </td>
                      <td className="nl-date">
                        {formatDate(notification.timeSent || notification.scheduledAt)}
                      </td>
                      <td className="nl-num">{formatCount(notification.reach)}</td>
                      <td className="nl-num">{formatCount(notification.view)}</td>
                      <td className="nl-num">{formatCount(notification.click)}</td>
                      <td className="nl-status-cell">{statusBadge(notification)}</td>
                      {automaticTab ? (
                        // Automatic notifications are view-only.
                        <td className="nl-menu-cell">{viewLink(notification)}</td>
                      ) : (
                        <Fragment>
                          <td className="nl-actor-cell" title={creator}>
                            {creator || "—"}
                          </td>
                          <td className="nl-date">{formatDate(notification.updatedAt)}</td>
                          {/* A sent campaign is history and can only be viewed (no hide from inbox),
                              so it gets the plain View link; one not sent yet keeps the menu. */}
                          {delivered ? (
                            <td className="nl-menu-cell">{viewLink(notification)}</td>
                          ) : (
                            <td className="nl-menu-cell">
                              <div className="nl-menu-wrap">
                                <button
                                  type="button"
                                  className="nl-kebab"
                                  aria-label={`Actions for ${notification.header}`}
                                  aria-haspopup="menu"
                                  aria-expanded={oMenu === notification.id}
                                  onClick={event => toggleMenu(event, notification.id)}
                                >
                                  ⋮
                                </button>
                                {oMenu === notification.id ? (
                                  <div className={oMenuUp ? "nl-row-menu up" : "nl-row-menu"} role="menu">
                                    <Link
                                      role="menuitem"
                                      to={`/PushNoti_Compose?id=${notification.id}`}
                                      onClick={() => SETxMenu(null)}
                                    >
                                      Edit
                                    </Link>
                                    {cancelled ? (
                                      <button type="button" role="menuitem" onClick={() => ask("RESUME", notification)}>
                                        Resume
                                      </button>
                                    ) : scheduled ? (
                                      <button type="button" role="menuitem" onClick={() => ask("CANCEL", notification)}>
                                        Cancel
                                      </button>
                                    ) : notification.status === "ACTIVE" ? (
                                      <button type="button" role="menuitem" onClick={() => ask("SEND", notification)}>
                                        Send now
                                      </button>
                                    ) : null}
                                    <button
                                      type="button"
                                      role="menuitem"
                                      className="nl-danger"
                                      onClick={() => ask("DELETE", notification)}
                                    >
                                      Delete
                                    </button>
                                  </div>
                                ) : null}
                              </div>
                            </td>
                          )}
                        </Fragment>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {!REQStrNotificationList && visibleRows.length === 0 ? (
              <div className="nl-empty">
                <img src={Images.noData.board} alt="" />
                <p className="nl-empty-title">
                  {hasFilters
                    ? "No results match your filters"
                    : automaticTab
                    ? "No automatic notifications yet"
                    : "No notifications yet"}
                </p>
                <p className="nl-empty-sub">
                  {hasFilters
                    ? "Try another search or clear the filters."
                    : automaticTab
                    ? "They appear here once a customer event raises one."
                    : "Use “Create new” to compose one."}
                </p>
              </div>
            ) : null}
          </div>

          {total > 0 ? (
            <div className="nl-pager">
              <span className="nl-pager-info">
                {rangeStart}–{rangeEnd} of {total}
              </span>
              <div className="nl-pager-btns">
                <button
                  type="button"
                  className="nl-pager-btn"
                  disabled={oPage <= 1}
                  onClick={() => SETxPage(oPage - 1)}
                >
                  ‹ Prev
                </button>
                <span className="nl-pager-page">
                  {(Pagination && Pagination.page) || oPage} / {totalPages || 1}
                </span>
                <button
                  type="button"
                  className="nl-pager-btn"
                  disabled={oPage >= totalPages}
                  onClick={() => SETxPage(oPage + 1)}
                >
                  Next ›
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {oConfirm ? (
        (() => {
          const meta = CONFIRM_META[oConfirm.action](oConfirm.notification);
          return (
            <ConfirmDialog
              title={meta.title}
              labelId="noti-confirm-title"
              onDismiss={() => SETxConfirm(null)}
              actions={[
                { label: meta.dismiss || "Cancel", autoFocus: true, onClick: () => SETxConfirm(null) },
                { label: meta.confirm, primary: true, onClick: onConfirm }
              ]}
            >
              {meta.body}
            </ConfirmDialog>
          );
        })()
      ) : null}
    </Fragment>
  );
}

const mapStateToProps = state => ({
  REQStrNotificationList: state.notificationListReducer.REQStrNotificationList,
  NotificationList: state.notificationListReducer.NotificationList,
  Pagination: state.notificationListReducer.Pagination,
  errorMessage: state.notificationListReducer.errorMessage
});

const mapDispatchToProps = {
  REQNotificationList,
  SENDxNotification,
  DELxNotification,
  SETxNotificationStatus,
  CleanUpNotificationList
};

const ConnectedNotificationList = compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(NotificationList);

// Every event the backend can raise, in be-notification's NotificationEventType order, with the content
// type its template gives the notification (be-notification src/config/NotificationTemplate.ts).
const AUTOMATIC_EVENTS = [
  { code: "ACCOUNT_OPENING_PROGRESS", contentType: "GENERAL" },
  { code: "ACCOUNT_APPROVED", contentType: "GENERAL" },
  { code: "MF_TRANSACTION_STATUS", contentType: "GENERAL" },
  { code: "PVD_TRANSACTION_STATUS", contentType: "GENERAL" },
  { code: "NDID_ABANDONED", contentType: "GENERAL" },
  { code: "KYC_UPDATED", contentType: "GENERAL" },
  { code: "PASSWORD_CHANGED", contentType: "URGENT" },
  { code: "NEW_DEVICE_LOGIN", contentType: "URGENT" },
  { code: "MOBILE_CHANGED", contentType: "URGENT" },
  { code: "CRS_FATCA_UPDATED", contentType: "GENERAL" },
  { code: "RSP_STATUS", contentType: "GENERAL" },
  { code: "REDEMPTION_BANK_STATUS", contentType: "GENERAL" },
  { code: "ACCOUNT_REJECTED_ID", contentType: "URGENT" },
  { code: "SUITABILITY_UPDATED", contentType: "GENERAL" },
  { code: "PVD_REGISTRATION_SUCCESS", contentType: "GENERAL" },
  { code: "SOFT_ONBOARD_SUCCESS", contentType: "GENERAL" },
  { code: "CSAT_MF_ACCOUNT_APPROVED", contentType: "GENERAL" },
  { code: "CSAT_PVD_ACCOUNT_OPENED", contentType: "GENERAL" },
  { code: "CSAT_FIRST_MF_TRANSACTION", contentType: "GENERAL" },
  { code: "CSAT_FIRST_PVD_TRANSACTION", contentType: "GENERAL" },
  { code: "CSAT_VIEW_PORTFOLIO", contentType: "GENERAL" }
];

// The five CSAT invitations, each with its own on / off switch on this page.
const CSAT_EVENT_CODES = [
  "CSAT_MF_ACCOUNT_APPROVED",
  "CSAT_PVD_ACCOUNT_OPENED",
  "CSAT_FIRST_MF_TRANSACTION",
  "CSAT_FIRST_PVD_TRANSACTION",
  "CSAT_VIEW_PORTFOLIO"
];

// The breadcrumb's last step on the Automatic page: "Detail <event>" for ?view=report&event=CSAT_CODE,
// "History <event>" or "History" for ?view=log[&event=CODE], and "event" on the Events list.
export const automaticTrail = search => {
  const query = new URLSearchParams(search);
  const view = query.get("view");
  const code = query.get("event") || "";
  if (view === "report" && CSAT_EVENT_CODES.includes(code)) {
    return `detail ${EVENT_LABEL[code]}`;
  }
  if (view === "log") {
    return EVENT_LABEL[code] ? `History ${EVENT_LABEL[code]}` : "History";
  }
  return "event";
};

// The Events list remembers how far its table was scrolled when a Detail page is opened, so Back lands
// where the operator was. The table (.nl-table-wrap) is the scroll container, not the window.
const EVENTS_SCROLL_KEY = "automaticEventsScrollTop";
const rememberEventsScroll = () => {
  const table = document.querySelector(".nl-table-wrap");
  try {
    sessionStorage.setItem(EVENTS_SCROLL_KEY, String(table ? table.scrollTop : 0));
  } catch (error) {
    // Storage can be blocked; Back then opens at the top, as before.
  }
};
const restoreEventsScroll = () => {
  let saved = null;
  try {
    saved = sessionStorage.getItem(EVENTS_SCROLL_KEY);
    sessionStorage.removeItem(EVENTS_SCROLL_KEY);
  } catch (error) {
    return;
  }
  const table = document.querySelector(".nl-table-wrap");
  if (table && saved !== null) {
    table.scrollTop = Number(saved);
  }
};

const AUTOMATIC_VIEWS = [
  { value: "events", label: "Events" },
  { value: "log", label: "History" }
];

// Auto send sits in the list too, beside Count, for the CSAT events (the others have no switch). A change
// asks first, through the same confirm as the Detail page's switch (onAskCsat).
const AutomaticEventList = ({ events, counts, csatEvents, csatReady, onAskCsat }) => (
  <div className="nl-panel" role="tabpanel">
    <div className="nl-table-wrap nl-events-table">
      <table>
        <thead>
          <tr>
            <th className="nl-num">No.</th>
            <th>Event</th>
            <th>Event code</th>
            <th>Content type</th>
            <th className="nl-num">Count</th>
            <th className="nl-switch-cell">Auto send</th>
            <th className="nl-actions-head">History</th>
            <th className="nl-actions-head">Detail</th>
          </tr>
        </thead>
        <tbody>
          {events.map(({ code, contentType }, index) => (
            <tr key={code}>
              <td className="nl-num">{index + 1}</td>
              <td>{EVENT_LABEL[code]}</td>
              <td className="nl-code">{code}</td>
              <td className="nl-type-cell">{CONTENT_TYPE_LABEL[contentType] || contentType}</td>
              <td className="nl-num">{counts[code] === undefined || counts[code] === null ? "—" : counts[code]}</td>
              <td className="nl-switch-cell">
                {CSAT_EVENT_CODES.includes(code) ? (
                  <div className={csatEvents[code] !== false ? "survey-switch-track survey-switch-track--active" : "survey-switch-track"}>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={csatEvents[code] !== false}
                      aria-label={`Auto send for ${EVENT_LABEL[code]}`}
                      className={csatEvents[code] !== false ? "survey-switch-thumb survey-switch-thumb--active" : "survey-switch-thumb"}
                      disabled={!csatReady}
                      onClick={() => onAskCsat({ code, on: csatEvents[code] === false })}
                    >
                      {csatEvents[code] !== false ? "Active" : "Inactive"}
                    </button>
                  </div>
                ) : (
                  "—"
                )}
              </td>
              <td className="nl-menu-cell">
                <Link className="nl-action-view" to={`/Notification_List/automatic?view=log&event=${code}`}>
                  View
                </Link>
              </td>
              <td className="nl-menu-cell">
                {CSAT_EVENT_CODES.includes(code) ? (
                  <Link
                    className="nl-action-view"
                    to={`/Notification_List/automatic?view=report&event=${code}`}
                    onClick={rememberEventsScroll}
                  >
                    Detail
                  </Link>
                ) : (
                  "—"
                )}
              </td>
            </tr>
          ))}
          {events.length === 0 ? (
            <tr>
              <td colSpan={8}>No events match your filters.</td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  </div>
);

// The Automatic menu entry: an Events list of every event, and the History (the automatic notifications
// already raised) as a tab. ?view=log&event=CODE opens the log filtered to one event.
const AutomaticPage = ({ location, EventCounts, REQEventCounts, CsatSetting, CsatSettingError, REQCsatSetting, REQSaveCsatSetting }) => {
  const query = new URLSearchParams(location.search);
  // ?view=report&event=CODE opens one CSAT event's report; an unknown event falls back to the list.
  const reportCode = query.get("view") === "report" && CSAT_EVENT_CODES.includes(query.get("event")) ? query.get("event") : "";
  const view = query.get("view") === "log" ? "log" : reportCode ? "report" : "events";
  const activeTab = view === "log" ? "log" : "events";
  // Events view only: the search box and the Event dropdown narrow the list below.
  const [oEventSearch, SETxEventSearch] = useState("");
  const [oEventCode, SETxEventCode] = useState("");

  // Back from a Detail page returns to the Events list where it was left; a Detail page opens at the top.
  useEffect(() => {
    if (view === "events") {
      restoreEventsScroll();
    }
    if (view === "report") {
      window.scrollTo(0, 0);
    }
  }, [view]);

  // The per-event counts are read once when the Events view opens. The CSAT switches are read on the
  // Events view and on a report, where each event's switch sits.
  useEffect(() => {
    if (view === "events") {
      REQEventCounts(AUTOMATIC_EVENTS.map(({ code }) => code));
    }
    if (view === "events" || view === "report") {
      REQCsatSetting();
    }
  }, [view]);

  // The View portfolio day limit, as the admin last saved it.
  const [oPortfolioDays, SETxPortfolioDays] = useState("0");
  useEffect(() => {
    if (CsatSetting) {
      SETxPortfolioDays(String(CsatSetting.viewPortfolioDays || 0));
    }
  }, [CsatSetting]);

  // A save counts as done when the setting comes back from the server; a failed save shows the error line instead.
  const [oLimitSaving, SETxLimitSaving] = useState(false);
  const [oShwLimitSaved, SETxShwLimitSaved] = useState(false);
  useEffect(() => {
    if (oLimitSaving && CsatSetting) {
      SETxLimitSaving(false);
      SETxShwLimitSaved(true);
    }
  }, [CsatSetting]);
  useEffect(() => {
    if (oLimitSaving && CsatSettingError) {
      SETxLimitSaving(false);
    }
  }, [CsatSettingError]);

  const csatEvents = (CsatSetting && CsatSetting.events) || {};
  // Who last changed the CSAT settings, shown beside the switch. The backend keeps one updatedBy for
  // every switch and the View portfolio limit together, as the old single switch on the CSAT page did.
  const csatChangedBy = adminUsername(CsatSetting && CsatSetting.updatedBy);
  // Every Auto send change, on or off, asks first: { code, on } is the change waiting for an answer.
  const [oCsatConfirm, SETxCsatConfirm] = useState(null);
  const onToggleCsat = (code, value) => {
    const eventCode = AUTOMATIC_EVENTS.find(event => event.code === code)?.code;
    if (!eventCode) return;
    REQSaveCsatSetting({ events: { [eventCode]: value } });
  };
  const onSaveLimit = event => {
    event.preventDefault();
    const days = Number(oPortfolioDays);
    if (Number.isInteger(days) && days >= 0 && days <= 365) {
      SETxLimitSaving(true);
      REQSaveCsatSetting({ viewPortfolioDays: days });
    }
  };

  const keyword = oEventSearch.trim().toLowerCase();
  const events = AUTOMATIC_EVENTS.filter(({ code }) => {
    if (oEventCode && code !== oEventCode) {
      return false;
    }
    return !keyword || [EVENT_LABEL[code], code].join(" ").toLowerCase().includes(keyword);
  });

  // Same page frame as the list (gutters and top padding under the breadcrumb). The Log view brings its own
  // frame, which style.scss flattens when it sits inside this one. The tabs come after the filters in both views.
  const tabs = (
    <div className="nl-tabs" role="tablist" aria-label="Automatic view">
      {AUTOMATIC_VIEWS.map(item => (
        <Link
          key={item.value}
          to={item.value === "log" ? "/Notification_List/automatic?view=log" : "/Notification_List/automatic"}
          role="tab"
          aria-selected={item.value === activeTab}
          className={item.value === activeTab ? "nl-tab active" : "nl-tab"}
        >
          {item.label}
        </Link>
      ))}
    </div>
  );

  return (
    <div
      className={
        view === "report"
          ? "noti-list-container noti-list-container-actions-only noti-list-container-report"
          : "noti-list-container noti-list-container-actions-only"
      }
    >
      {view === "log" ? (
        <ConnectedNotificationList source="automatic" eventType={query.get("event") || ""} tabs={tabs} />
      ) : view === "report" ? (
        <Fragment>
          {/* The Back button sits on the breadcrumb's line, where the other pages keep their action button. */}
          <div className="noti-list-topbar noti-list-topbar-actions-only">
            <Link className="nl-report-back" to="/Notification_List/automatic">
              Back
            </Link>
          </div>
          {/* The date filter sits where the list's filters sit; the tabs and this event's title follow it. */}
          <SurveyReport
            key={reportCode}
            eventType={reportCode}
            embedded
            filterAside={
              <Fragment>
                {csatChangedBy ? (
                  <span className="survey-switch-changed">
                    Changed by {csatChangedBy} · {formatDate(CsatSetting.updatedAt)}
                  </span>
                ) : null}
                <span className="survey-switch-label" id="nl-report-switch-label">
                  Auto send
                </span>
                <div className={csatEvents[reportCode] !== false ? "survey-switch-track survey-switch-track--active" : "survey-switch-track"}>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={csatEvents[reportCode] !== false}
                    aria-labelledby="nl-report-switch-label"
                    className={csatEvents[reportCode] !== false ? "survey-switch-thumb survey-switch-thumb--active" : "survey-switch-thumb"}
                    disabled={!CsatSetting}
                    onClick={() => SETxCsatConfirm({ code: reportCode, on: csatEvents[reportCode] === false })}
                  >
                    {csatEvents[reportCode] !== false ? "Active" : "Inactive"}
                  </button>
                </div>
              </Fragment>
            }
            tabs={
              <Fragment>
                <div className="nl-report-head">
                  <p className="nl-report-title">{EVENT_LABEL[reportCode]}</p>
                </div>
                {reportCode === "CSAT_VIEW_PORTFOLIO" ? (
                  <form className="nl-filter-row nl-limit-row" onSubmit={onSaveLimit}>
                    <div className="nl-filter-field">
                      <span className="nl-filter-label">View portfolio – Send survey every (days)</span>
                      <span className="nl-limit-hint">
                        Counted from the last time the survey was sent, whether answered or not (e.g. 5 = every 5 days, 30 = every 30 days). 0 = send every time.
                      </span>
                      <input
                        className="nl-date-input"
                        type="number"
                        min="0"
                        max="365"
                        aria-label="View portfolio limit in days"
                        value={oPortfolioDays}
                        onChange={event => SETxPortfolioDays(event.target.value)}
                      />
                    </div>
                    <div className="nl-filter-actions">
                      <button type="submit" className="nl-clear" disabled={!CsatSetting || oLimitSaving}>
                        Save limit
                      </button>
                    </div>
                  </form>
                ) : null}
                {/* Always rendered under the title, so a failed save never moves the figures. */}
                <div className="nl-csat-status" role="status">
                  {CsatSettingError || ""}
                </div>
              </Fragment>
            }
          />
        </Fragment>
      ) : (
        <Fragment>
          {/* Empty bar, as on the list page: keeps the filters below the breadcrumb line. */}
          <div className="noti-list-topbar noti-list-topbar-actions-only" />
          <div className="nl-filter-row">
            <div className="nl-filter-field nl-filter-field-wide">
              <span className="nl-filter-label">Search</span>
              <form className="nl-search" onSubmit={event => event.preventDefault()}>
                <Icon name="search" size={18} />
                <input
                  type="search"
                  placeholder="Search by event name or code…"
                  value={oEventSearch}
                  onChange={event => SETxEventSearch(event.target.value)}
                />
              </form>
            </div>
            <div className="nl-filter-field nl-filter-field-event">
              <span className="nl-filter-label">Event</span>
              <Select
                className="basic-single"
                classNamePrefix="select"
                aria-label="Filter by event"
                isSearchable={false}
                options={EVENT_FILTER_OPTIONS}
                value={EVENT_FILTER_OPTIONS.find(option => option.value === oEventCode) || null}
                onChange={option => SETxEventCode(option ? option.value : "")}
              />
            </div>
          </div>
          {tabs}
          <AutomaticEventList
            events={events}
            counts={EventCounts || {}}
            csatEvents={csatEvents}
            csatReady={Boolean(CsatSetting)}
            onAskCsat={SETxCsatConfirm}
          />
        </Fragment>
      )}
      {oShwLimitSaved ? (
        <ConfirmDialog
          title="Saved"
          labelId="nl-limit-saved-title"
          onDismiss={() => SETxShwLimitSaved(false)}
          actions={[{ label: "OK", onClick: () => SETxShwLimitSaved(false), primary: true, autoFocus: true }]}
        >
          View portfolio now sends the survey every {oPortfolioDays} days (0 = send every time).
        </ConfirmDialog>
      ) : null}
      {oCsatConfirm ? (
        <ConfirmDialog
          title={`Turn ${oCsatConfirm.on ? "on" : "off"} Auto send for ${EVENT_LABEL[oCsatConfirm.code]}?`}
          labelId="nl-csat-confirm-title"
          onDismiss={() => SETxCsatConfirm(null)}
          actions={[
            { label: "Cancel", onClick: () => SETxCsatConfirm(null), autoFocus: true },
            {
              label: oCsatConfirm.on ? "Turn on" : "Turn off",
              onClick: () => {
                onToggleCsat(oCsatConfirm.code, oCsatConfirm.on);
                SETxCsatConfirm(null);
              },
              primary: true
            }
          ]}
        >
          {oCsatConfirm.on
            ? "Customers get this CSAT survey again each time the event happens."
            : "Customers stop getting this CSAT survey until it is turned back on. Surveys already in their inbox can still be answered."}
        </ConfirmDialog>
      ) : null}
    </div>
  );
};

// Sidebar entries under Push Notification: the same list, with the tab fixed by the route.
export const NotificationListManual = props => <ConnectedNotificationList {...props} source="manual" />;
const ConnectedAutomaticPage = connect(
  state => ({
    EventCounts: state.notificationListReducer.EventCounts,
    CsatSetting: state.notificationListReducer.CsatSetting,
    CsatSettingError: state.notificationListReducer.CsatSettingError
  }),
  { REQEventCounts, REQCsatSetting, REQSaveCsatSetting }
)(AutomaticPage);

export const NotificationListAutomatic = props => <ConnectedAutomaticPage {...props} />;

export default ConnectedNotificationList;
